import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import { loadErrorMessage } from '../../utils/loadState'
import { getCustomer, getWorkplan, adjustWorkplan, addWorkplanGate, getWorkplanCost, workplanToThread } from '../../api'
import './Workplan.css'

export default function Workplan() {
  const { customerId, workplanId } = useParams()
  const navigate = useNavigate()
  const [customer, setCustomer] = useState(null)
  const [workplan, setWorkplan] = useState(null)
  const [cost, setCost] = useState(null)
  const [loading, setLoading] = useState(true)
  // A failed load is an error with a Retry, never "Workplan not found." (item 201).
  const [workplanError, setWorkplanError] = useState(null)

  async function load() {
    setLoading(true)
    try {
      const [ctx, wp, wc] = await Promise.allSettled([
        getCustomer(customerId),
        getWorkplan(workplanId),
        getWorkplanCost(workplanId),
      ])
      if (ctx.status === 'fulfilled') setCustomer(ctx.value)
      if (wp.status === 'fulfilled') { setWorkplan(wp.value); setWorkplanError(null) }
      else { setWorkplan(null); setWorkplanError(loadErrorMessage(wp.reason)) }
      if (wc.status === 'fulfilled') setCost(wc.value)
    } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [customerId, workplanId])

  async function handleConvertToThread() {
    try {
      const result = await workplanToThread(workplanId)
      const tid = result.thread_id || result.id
      if (tid) navigate(`/threads/${tid}`)
      else alert('Converted successfully')
    } catch (err) { alert('Convert failed: ' + err.message) }
  }

  const companyName = customer?.company_name || customerId
  const breadcrumbs = [
    { label: 'Customers', href: '/customers' },
    { label: companyName, href: `/customers/${customerId}` },
    { label: 'Workplan' },
  ]

  if (loading) return <><TopNav breadcrumbs={breadcrumbs} /><div className="wp-loading"><div className="wp-spinner" /><p>Loading…</p></div></>

  if (workplanError) return (
    <><TopNav breadcrumbs={breadcrumbs} /><div className="wp-loading"><LoadError what="the workplan" error={workplanError} onRetry={load} /></div></>
  )
  if (!workplan) return (
    <><TopNav breadcrumbs={breadcrumbs} /><div className="wp-loading"><p>Workplan not found.</p><button className="wp-back" onClick={() => navigate(`/customers/${customerId}`)}>Back</button></div></>
  )

  const phases = workplan.phases || workplan.steps || []
  const totalHours = phases.reduce((sum, p) => sum + (p.estimated_hours || 0), 0)

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} actions={
        <>
          <button className="nav-btn" onClick={handleConvertToThread}>Save as Thread</button>
          <button className="nav-btn primary">Export</button>
          
        </>
      } />
      <div className="wp-page">
        <div className="wp-header">
          <h1 className="wp-title">{workplan.name || workplan.title || 'Workplan'}</h1>
          <p className="wp-subtitle">{workplan.description || `${phases.length} phases · ${totalHours} hours estimated`}</p>
        </div>

        {/* Stats */}
        <div className="wp-stats">
          <div className="wp-stat"><div className="wp-stat-value">{phases.length}</div><div className="wp-stat-label">Phases</div></div>
          <div className="wp-stat"><div className="wp-stat-value">{totalHours}h</div><div className="wp-stat-label">Estimated</div></div>
          {cost?.total_cost && <div className="wp-stat"><div className="wp-stat-value">${cost.total_cost.toLocaleString()}</div><div className="wp-stat-label">Est. Cost</div></div>}
        </div>

        {/* Phases */}
        <div className="wp-phases">
          {phases.map((phase, i) => (
            <div key={i} className="wp-phase">
              <div className="wp-phase-header">
                <div className="wp-phase-num">{i + 1}</div>
                <div className="wp-phase-info">
                  <div className="wp-phase-name">{phase.name || phase.title || `Phase ${i + 1}`}</div>
                  <div className="wp-phase-meta">
                    {phase.estimated_hours && `${phase.estimated_hours}h`}
                    {phase.priority && ` · Priority: ${phase.priority}`}
                    {phase.status && ` · ${phase.status}`}
                  </div>
                </div>
              </div>
              {phase.description && <div className="wp-phase-desc">{phase.description}</div>}
              {phase.tasks && phase.tasks.length > 0 && (
                <div className="wp-tasks">
                  {phase.tasks.map((task, j) => (
                    <div key={j} className="wp-task">
                      <span className="wp-task-bullet">•</span>
                      <span className="wp-task-text">{task.description || task.name || task}</span>
                      {task.hours && <span className="wp-task-hours">{task.hours}h</span>}
                    </div>
                  ))}
                </div>
              )}
              {phase.gate && (
                <div className="wp-gate">🚧 Gate: {phase.gate}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  )
}
